import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Gauge, RefreshCw, Printer, Loader2, Mail } from "lucide-react";
import { C, ScoreRing, openPrintReport } from "./seoUi";
import {
  OverviewTab, PageAuditTab, IssuesTab, LocalSeoTab, GapsTab, LinksTab, SchemaTab, IntegrationsTab,
} from "./ReportTabs";
import KeywordsPanel from "./KeywordsPanel";
import CompetitorsPanel from "./CompetitorsPanel";
import TasksPanel from "./TasksPanel";
import EmailReportDialog from "./EmailReportDialog";
import { HelpButton } from "@/components/help/HelpButton";

const TABS = [
  ["overview", "Overview"], ["pages", "Page Audit"], ["issues", "Technical"], ["local", "Local SEO"],
  ["keywords", "Keywords"], ["gaps", "Content Gaps"], ["links", "Links"], ["schema", "Schema"],
  ["competitors", "Competitors"], ["tasks", "Action Queue"], ["integrations", "Integrations"],
];

const EMAIL_STATUS = {
  queued: { c: C.amber, label: "queued" },
  sending: { c: C.amber, label: "sending" },
  sent: { c: C.green, label: "sent" },
  failed: { c: C.red, label: "failed" },
  skipped: { c: C.slate, label: "skipped" },
};

export default function SeoIntelligence() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [franchise, setFranchise] = useState("__all__");
  const [pageType, setPageType] = useState("__all__");
  const [tab, setTab] = useState("overview");
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [taskReload, setTaskReload] = useState(0);
  const [emailOpen, setEmailOpen] = useState(false);
  const [lastEmail, setLastEmail] = useState(null);

  const fr = franchise === "__all__" ? "" : franchise;
  const pt = pageType === "__all__" ? "" : pageType;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fr) params.franchise = fr;
      if (pt) params.page_type = pt;
      const { data } = await api.get("/seo-intelligence/report", { params });
      setReport(data);
    } catch (e) { toast.error(errMsg(e, "Could not load the SEO report.")); }
    finally { setLoading(false); }
  }, [fr, pt]);
  useEffect(() => { load(); }, [load]);

  const loadLastEmail = useCallback(async () => {
    try {
      const { data } = await api.get("/seo-intelligence/report/last-email");
      setLastEmail(data);
    } catch { /* non-blocking */ }
  }, []);
  useEffect(() => { loadLastEmail(); }, [loadLastEmail]);

  const generateAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/seo-intelligence/ai-summary", null, { params: fr ? { franchise: fr } : {} });
      setAiSummary(data);
    } catch (e) { toast.error(errMsg(e, "AI summary unavailable. Rules-based recommendations still apply.")); }
    finally { setAiLoading(false); }
  };

  const createTask = async (payload) => {
    try {
      await api.post("/seo-intelligence/tasks", payload);
      toast.success("Added to Action Queue.");
      setTaskReload((n) => n + 1);
    } catch (e) { toast.error(errMsg(e, "Could not create task.")); }
  };

  const meta = report?.meta || {};
  const scope = report?.scope || {};
  const franchises = meta.franchises || [];
  const scopeLabel = scope.is_super
    ? (fr ? (franchises.find((f) => f.id === fr)?.name || "Territory") : "All territories (global)")
    : `${scope.franchise_count || 0} ${scope.franchise_count === 1 ? "territory" : "territories"} in your scope`;

  if (loading && !report) return <div className="flex justify-center py-24"><Loader2 className="animate-spin" style={{ color: C.navy }} size={30} /></div>;

  return (
    <div className="max-w-7xl mx-auto pb-20" data-testid="seo-intelligence-page">
      {/* Header */}
      <div className="rounded-[0.9rem] border bg-white p-5" style={{ borderColor: C.line }}>
        <div className="flex items-start justify-between gap-5 flex-wrap">
          <div className="flex items-center gap-4">
            <ScoreRing score={report?.health?.score || 0} label={report?.health?.label} size={84} stroke={9} testid="header-score-ring" />
            <div>
              <div className="flex items-center gap-2">
                <Gauge size={20} style={{ color: C.navy }} />
                <h1 className="font-display font-bold text-2xl" style={{ color: C.navy }}>SEO Intelligence</h1>
              </div>
              <p className="text-[13px] mt-0.5" style={{ color: C.slate }}>
                {scopeLabel} · Internal data only — no rankings, traffic, or backlinks are fabricated.
              </p>
              {report?.generated_at && <p className="text-[11.5px] mt-0.5" style={{ color: C.slate }}>Updated {new Date(report.generated_at).toLocaleString()}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {franchises.length > 1 && (
              <Select value={franchise} onValueChange={setFranchise}>
                <SelectTrigger data-testid="franchise-filter" className="h-9 w-[190px] text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{scope.is_super ? "All territories" : "All my territories"}</SelectItem>
                  {franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={pageType} onValueChange={setPageType}>
              <SelectTrigger data-testid="pagetype-filter" className="h-9 w-[160px] text-[13px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All page types</SelectItem>
                {(meta.page_types || []).map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <HelpButton route="/seo-intelligence" testid="seo-help-button" />
            <Button data-testid="print-report-btn" size="sm" variant="outline" onClick={() => openPrintReport(report, scopeLabel)} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Printer size={14} /> Print / PDF</Button>
            <Button data-testid="email-report-btn" size="sm" variant="outline" onClick={() => setEmailOpen(true)} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Mail size={14} /> Email report</Button>
            <Button data-testid="refresh-report-btn" size="sm" onClick={load} disabled={loading} className="h-9 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white">{loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Refresh</Button>
            <div className="w-full flex justify-end mt-1">
              {lastEmail && (lastEmail.emailed ? (
                <span data-testid="last-emailed-chip" className="inline-flex items-center gap-1.5 text-[11.5px] px-2.5 py-1 rounded-full" style={{ backgroundColor: `${EMAIL_STATUS[lastEmail.status]?.c || C.slate}14`, color: EMAIL_STATUS[lastEmail.status]?.c || C.slate }}>
                  <Mail size={12} /> Last emailed {new Date(lastEmail.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} to {lastEmail.to} · {EMAIL_STATUS[lastEmail.status]?.label || lastEmail.status}
                </span>
              ) : (
                <span data-testid="last-emailed-chip" className="inline-flex items-center gap-1.5 text-[11.5px] px-2.5 py-1 rounded-full" style={{ backgroundColor: "rgba(75,85,99,0.10)", color: C.slate }}>
                  <Mail size={12} /> Not emailed yet
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="mt-5">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 justify-start">
          {TABS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}
              className="text-[13px] px-3 py-1.5 rounded-full border data-[state=active]:bg-[#1C4358] data-[state=active]:text-white data-[state=active]:border-[#1C4358]"
              style={{ borderColor: C.line, color: C.navy }}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {report && <>
          <TabsContent value="overview" className="mt-5">
            <OverviewTab report={report} aiSummary={aiSummary} aiLoading={aiLoading} onGenerateAi={generateAi} onCreateTask={createTask} scopeLabel={scopeLabel} />
          </TabsContent>
          <TabsContent value="pages" className="mt-5"><PageAuditTab report={report} onCreateTask={createTask} /></TabsContent>
          <TabsContent value="issues" className="mt-5"><IssuesTab report={report} /></TabsContent>
          <TabsContent value="local" className="mt-5"><LocalSeoTab report={report} /></TabsContent>
          <TabsContent value="keywords" className="mt-5"><KeywordsPanel franchise={fr} meta={meta} /></TabsContent>
          <TabsContent value="gaps" className="mt-5"><GapsTab report={report} onCreateTask={createTask} /></TabsContent>
          <TabsContent value="links" className="mt-5"><LinksTab report={report} /></TabsContent>
          <TabsContent value="schema" className="mt-5"><SchemaTab report={report} /></TabsContent>
          <TabsContent value="competitors" className="mt-5"><CompetitorsPanel franchise={fr} meta={meta} /></TabsContent>
          <TabsContent value="tasks" className="mt-5"><TasksPanel franchise={fr} reloadSignal={taskReload} /></TabsContent>
          <TabsContent value="integrations" className="mt-5"><IntegrationsTab report={report} /></TabsContent>
        </>}
      </Tabs>

      {report && <EmailReportDialog open={emailOpen} onOpenChange={setEmailOpen} report={report} franchise={fr} pageType={pt} scopeLabel={scopeLabel} onSent={loadLastEmail} />}
    </div>
  );
}
